import { apiFetch } from '../../lib/api';
import type { LearnerSummary } from './registration-api';

export interface PaymentSummary {
  paidToDateMinor: string;
  outstandingMinor: string;
  transactionCount: number;
  currency: string;
}

export interface ParentPayment {
  transactionId: string;
  paidAt: string;
  learnerId: string;
  learnerName: string;
  purpose: 'REGISTRATION' | 'SUBJECT_FEES';
  purposeLabel: string;
  amountMinor: string;
  currency: string;
  receiptId: string | null;
  receiptNumber: string | null;
}

export interface ParentPaymentsResponse {
  summary: PaymentSummary;
  items: ParentPayment[];
}

export interface TimelineEvent {
  type: string;
  title: string;
  timestamp: string;
  actorDisplayName?: string | null;
  note?: string | null;
}

export interface LearnerDetail {
  learner: {
    id: string;
    enrolmentNumber: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    gender: string | null;
    dateOfBirth: string;
    applicantSourceType: string;
    currentSchoolName: string | null;
    schoolAdmissionNumber: string | null;
    city: string;
    state: string;
    country: string | null;
    pincode: string;
  };
  application: {
    id: string;
    applicationNumber: string;
    status: string;
    offeringId: string;
    academicBagId: string | null;
    academicBagStatus?: string | null;
    documentsPolicy?: 'MANDATORY' | 'OPTIONAL';
  };
  academicLevel: { id: string; code: string; name: string } | null;
  school: { id: string | null; name: string | null } | null;
  subjects: Array<{
    id: string;
    subjectName: string;
    syllabusCode: string | null;
    itemStatus: string;
    rejectionReason: string | null;
    rejectionComment: string | null;
    rejectedAt: string | null;
    reviewerParty: string | null;
    stepName: string | null;
  }>;
  timeline: TimelineEvent[];
}

export interface ParentProfile {
  id: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  relationshipToLearner: string;
  email: string | null;
  countryCode: string;
  mobile: string | null;
  whatsappCountryCode: string | null;
  whatsappNumber: string | null;
}

export interface ReceiptSnapshot {
  id: string;
  receiptNumber: string;
  snapshotJson: Record<string, unknown>;
}

export async function fetchMyPayments(filters?: {
  learnerId?: string;
  purpose?: string;
}): Promise<ParentPaymentsResponse> {
  const params = new URLSearchParams();
  if (filters?.learnerId) params.set('learnerId', filters.learnerId);
  if (filters?.purpose) params.set('purpose', filters.purpose);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiFetch<ParentPaymentsResponse>(`/me/payments${query}`);
}

export async function fetchLearnerDetail(learnerId: string): Promise<LearnerDetail> {
  return apiFetch<LearnerDetail>(`/me/learners/${learnerId}`);
}

export async function fetchParentProfile(): Promise<ParentProfile> {
  return apiFetch<ParentProfile>('/registration/parent-guardian');
}

export async function patchParentProfile(body: Record<string, unknown>): Promise<ParentProfile> {
  return apiFetch<ParentProfile>('/registration/parent-guardian', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function fetchPaymentReceipt(transactionId: string): Promise<ReceiptSnapshot> {
  return apiFetch<ReceiptSnapshot>(`/payments/${transactionId}/receipt`);
}

export function learnerNavFromSummary(items: LearnerSummary[]): Array<{
  learnerId: string;
  firstName: string;
  lastName: string;
  applicationStatus: string;
}> {
  return items.map((item) => ({
    learnerId: item.learnerId,
    firstName: item.firstName,
    lastName: item.lastName,
    applicationStatus: item.applicationStatus,
  }));
}
